<template>
  <SrpModal v-model:isVisible="_isVisible" size="small">
    <template #content>
      <!-- Confirm deletion modal -->
      <div class="confirm-deletion-modal">
        <div
          :class="{
            'standard-page-header': true,
            'standard-page-header--no-bottom-line': true,
            'confirm-deletion-modal__header': true,
          }"
        >
          <h2 class="global-h2 standard-page-header__title">This will permanently delete this group{{ photosAndVideosCountString }}.<br /><br />Are you sure?</h2>
        </div>
      </div>
      <!-- / Confirm deletion modal -->
    </template>

    <template #footer="{ closeModal }">
      <SrpButton @click="closeModal" color="gray" fill="outlined" :isDisabled="isDeleting">Cancel</SrpButton>

      <SrpButton
        color="red"
        :isDisabled="isDeleting"
        @click="
          async () => {
            await deleteGroup();
            closeModal();
            emit('deletionCompleted');
          }
        "
      >
        <template #icon>
          <LoadingIcon v-if="isDeleting" :size="19" />
          <IconEmbedded v-else name="trashcan-alt_2-5" :size="19" />
        </template>
        Delete
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import FileUtils from "@logic/FileUtils";
import { PhotoGallerySource } from "@contracts/photosPageData";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

const props = withDefaults(
  defineProps<{
    customerId: string;
    groupId: string;
    allGroupFiles: Array<string>;
    currentOperationSource?: PhotoGallerySource | null;
  }>(),
  {
    customerId: "",
    groupId: "",
    allGroupFiles: () => [],
    currentOperationSource: null,
  }
);

const emit = defineEmits<{
  (e: "deletionCompleted"): void;
  (e: "deletionFailed404"): void;
}>();

// Photos and videos count string =============================================
const photosAndVideosCountString = computed<string>(() => {
  const imgCount = props.allGroupFiles.filter(f => !FileUtils.isVideoFileType(f)).length;
  const vidCount = props.allGroupFiles.length - imgCount;

  const nonBreakingSpace = String.fromCharCode(160);

  const imgCountHumanString = `${imgCount}${nonBreakingSpace}photo` + (imgCount > 1 ? "s" : "");
  const vidCountHumanString = `${imgCount}${nonBreakingSpace}video` + (vidCount > 1 ? "s" : "");

  if (imgCount && vidCount) {
    return ` and ${imgCountHumanString} and ${vidCountHumanString}`;
  } else if (imgCount) {
    return ` and ${imgCountHumanString}`;
  } else if (vidCount) {
    return ` and ${vidCountHumanString}`;
  } else {
    return "";
  }
});

// Private isVisible ==========================================================
const _isVisible = ref<boolean>(false);

// Delete photos & videos =====================================================
const isDeleting = ref<boolean>(false);

async function deleteGroup() {
  isDeleting.value = true;

  await PhotoGalleryV2Repo.deleteMyUploadsGroup(props.customerId, props.groupId, props.currentOperationSource);

  isDeleting.value = false;
}
</script>

<style scoped lang="scss">
// Confirm deletion form ======================================================
.confirm-deletion-modal {
  &__header {
    padding-bottom: 0 !important;
  }
}
</style>
